"use strict";
exports.__esModule = true;
var uuid_1 = require("uuid");
var _1ds_core_js_1 = require("@microsoft/1ds-core-js");
var Constants_1 = require("./Constants");
var Types_1 = require("./Types");
var ErrorPropertyHelpers_1 = require("./ErrorPropertyHelpers");
var Helpers_1 = require("./Helpers");
var Logging_1 = require("./Logging");
var EventProperties_1 = require("./EventProperties");
var GamingTelemetryProvider_1 = require("./telemetryProviders/GamingTelemetryProvider");
var Environment_1 = require("./telemetryProviders/upload/Environment");
var error_ActiveUserIdMissing = "GamingTelemetry: Please provide a value for activeUserId when calling setActiveUserId";
var error_DuplicatePrefix = function (componentName) {
    return "GamingTelemetry: registerProvider() automatically prefixes componentName with 'Microsoft.Gaming'. Computed provider namespace: Microsoft.Gaming." + componentName;
};
var error_ExtendedDataFull = "GamingTelemetry: Extended Data can only contain a maximum of " + Constants_1.EXTENDED_DATA_MAX_ENTRIES + " keys";
var error_ExtendedDataMissingKey = function (key) {
    return "GamingTelemetry: Unable to remove value. Extended Data does not contain a value for the following key: " + key;
};
var error_ExtendedDataReservedKey = function (key) {
    return "GamingTelemetry: Extended Data key '" + key + "' should only be managed by its associated API";
};
var error_IdentifierMissing = "GamingTelemetry: An error identifier must be specified.";
var error_MissingFeatureName = "GamingTelemetry: A valid featureName must be specified if countAsFeatureUsage is true";
var error_MultipleErrorLevels = "GamingTelemetry: More than one ErrorLevel flag specified on 'properties'.";
var error_MultipleErrorTypes = "GamingTelemetry: More than one ErrorType flag specified on 'properties'.";
var error_ProviderGroupOverridesUnsupported = "GamingTelemetry: The provider group overrides are not supported registering with enforcement";
var error_Unregistered = "GamingTelemetry: Cannot send an event before RegisterProvider() has been called.";
var error_PropertyChangeForInvalidEvent = function (eventType) {
    return "GamingTelemetry: Cannot change properties for GtlEventType." + eventType;
};
var error_InvalidKeywordForRequiredEvent = "GamingTelemetry: Required events must include the 'Critical' EvenKeyword.";
var telemetryProvider;
var autoTerminateSessions = false;
var sessionStartTime;
var getCommonProperties = function () {
    return {
        extendedData: EventProperties_1.ComponentEventProperties.ExtendedData.GetForEvent()
    };
};
var registerProviderShared = function (componentName, usingUnsupportedMobileAPI, registrationOptions, deviceInteractionVectorOptions, hasOptionalDataConsentCallback) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    if (componentName.startsWith("Microsoft.")) {
        throw new Error(error_DuplicatePrefix(componentName));
    }
    var apiLogger = new Logging_1.LogProvider("API", (_a = registrationOptions) === null || _a === void 0 ? void 0 : _a.Logger);
    autoTerminateSessions = !((_b = registrationOptions) === null || _b === void 0 ? void 0 : _b.DisableAutoSessionCompletion);
    var isRequiredData = (_c = registrationOptions) === null || _c === void 0 ? void 0 : _c.IsRequiredData;
    EventProperties_1.ComponentEventProperties.Latency.ProviderOverride = ((_d = registrationOptions) === null || _d === void 0 ? void 0 : _d.IsRealtime) ? _1ds_core_js_1.EventLatency.RealTime
        : undefined;
    EventProperties_1.ComponentEventProperties.Keywords.ProviderOverride =
        isRequiredData || ((_e = registrationOptions) === null || _e === void 0 ? void 0 : _e.IsCriticalData)
            ? Types_1.EventKeyword.Critical
            : undefined;
    EventProperties_1.ComponentEventProperties.Persistence.ProviderOverride = ((_f = registrationOptions) === null || _f === void 0 ? void 0 : _f.IsCriticalPersistence) ? _1ds_core_js_1.EventPersistence.Critical
        : undefined;
    EventProperties_1.ComponentEventProperties.GroupIdType.ProviderOverride = isRequiredData
        ? Types_1.GtlGroupIdType.CoreRequired
        : undefined;
    EventProperties_1.ComponentEventProperties.ComponentName = componentName;
    EventProperties_1.ClickstreamEventProperties.PreserveExistingCorrelationVector = (_h = (_g = deviceInteractionVectorOptions) === null || _g === void 0 ? void 0 : _g.PreserveExistingCorrelationVector, (_h !== null && _h !== void 0 ? _h : false));
    EventProperties_1.ClickstreamEventProperties.IncrementCallback = (_j = deviceInteractionVectorOptions) === null || _j === void 0 ? void 0 : _j.IncrementCallback;
    var commonProperties = getCommonProperties();
    telemetryProvider = GamingTelemetryProvider_1.GamingTelemetryProvider.RegisterProvider(commonProperties, registrationOptions, hasOptionalDataConsentCallback);
    if (usingUnsupportedMobileAPI) {
        var message = 'Callers should move to "registerProviderWithCallerEnforcement" to incorporate the user\'s Optional Data Consent.';
        apiLogger.warn("Usage of this API is unsupported on Mobile, and will be deprecated in a future release.\n" + message);
        telemetryProvider.WriteCriticalError(commonProperties, {
            identifier: "Unsupported Mobile registration",
            message: message
        });
    }
    apiLogger.info("GTL Provider for " + componentName + " registered.");
};
var isDeviceInteraction = function (eventKeyword) {
    return (Helpers_1.doLargeBitwiseOp([eventKeyword, Types_1.EventKeyword.DeviceInteractionData], "&") === Types_1.EventKeyword.DeviceInteractionData);
};
var getPropertiesForEventWithDIV = function (eventType, DIV) {
    var keyword = EventProperties_1.ComponentEventProperties.Keywords.GetForEvent(eventType);
    keyword = Helpers_1.doLargeBitwiseOp([keyword, Types_1.EventKeyword.DeviceInteractionData], "|");
    return {
        eventName: eventType,
        groupIdType: EventProperties_1.ComponentEventProperties.GroupIdType.GetForEvent(eventType),
        privacyTags: EventProperties_1.ComponentEventProperties.PrivacyTags.GetForEvent(eventType),
        latency: EventProperties_1.ComponentEventProperties.Latency.GetForEvent(eventType),
        persistence: EventProperties_1.ComponentEventProperties.Persistence.GetForEvent(eventType),
        keyword: keyword,
        DIV: DIV
    };
};
var getDivForEvent = function (providerForOverflow, propertiesForOverflow, allowExtension) {
    if (allowExtension === void 0) { allowExtension = true; }
    if (!EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.CanUpdate(allowExtension)) {
        var oldCv = EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.Get();
        EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.Reseed();
        providerForOverflow.WriteRelatedCv(propertiesForOverflow, getPropertiesForEventWithDIV(Types_1.GtlEventType.RelatedCv, EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.UpdateAndGet()), oldCv);
        allowExtension = false;
    }
    return EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.UpdateAndGet(allowExtension);
};
var getPropertiesForEvent = function (eventType, providerForDivOverflow, propertiesForDivOverflow) {
    var keyword = EventProperties_1.ComponentEventProperties.Keywords.GetForEvent(eventType);
    var DIV;
    if (isDeviceInteraction(keyword)) {
        DIV = getDivForEvent(providerForDivOverflow, propertiesForDivOverflow, false);
    }
    return {
        eventName: eventType,
        groupIdType: EventProperties_1.ComponentEventProperties.GroupIdType.GetForEvent(eventType),
        privacyTags: EventProperties_1.ComponentEventProperties.PrivacyTags.GetForEvent(eventType),
        latency: EventProperties_1.ComponentEventProperties.Latency.GetForEvent(eventType),
        persistence: EventProperties_1.ComponentEventProperties.Persistence.GetForEvent(eventType),
        keyword: keyword,
        DIV: DIV
    };
};
var updateActiveUserId = function (telemetryProvider, activeUserId) {
    EventProperties_1.ComponentEventProperties.ExtendedData.ActiveUserId = activeUserId;
    var commonProperties = getCommonProperties();
    telemetryProvider.WriteActiveUserUpdated(commonProperties, getPropertiesForEvent(Types_1.GtlEventType.ActiveUserUpdated, telemetryProvider, commonProperties));
};
var writeSessionStopAndUpdateSession = function (telemetryProvider, failed, sessionState, sessionDuration, customAttributes) {
    var activeSessionSummaries = EventProperties_1.ComponentEventProperties.ActiveSessionSummaries.GetForEvent();
    var sessionStopPayload = {
        duration: sessionDuration,
        errorSummary: activeSessionSummaries.errorCodes,
        errorIdentifierSummary: activeSessionSummaries.errorIdentifiers,
        failed: failed,
        featureUsageSummary: activeSessionSummaries.featureUsage,
        state: sessionState,
        totalErrorCount: activeSessionSummaries.totalErrorCount,
        customAttributes: customAttributes
    };
    var commonProperties = getCommonProperties();
    telemetryProvider.WriteSessionStop(commonProperties, getPropertiesForEvent(Types_1.GtlEventType.SessionStopped, telemetryProvider, commonProperties), sessionStopPayload);
    EventProperties_1.ComponentEventProperties.ExtendedData.SessionId = undefined;
};
var truncateSessionIfActive = function (telemetryProvider, stopTime) {
    if (EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
        var sessionDuration = void 0;
        if (sessionStartTime) {
            sessionDuration = stopTime - sessionStartTime;
        }
        else {
            sessionDuration = 0;
        }
        writeSessionStopAndUpdateSession(telemetryProvider, true, "SessionTruncatedAlreadyRunning", sessionDuration);
    }
};
exports.registerProvider = function (componentName, registrationOptions, deviceInteractionVectorOptions) {
    registerProviderShared(componentName, Environment_1.isReactNativeMobile(), registrationOptions, deviceInteractionVectorOptions);
};
exports.registerProviderWithCallerEnforcement = function (componentName, hasOptionalDataConsentCallback, registrationOptions, deviceInteractionVectorOptions) {
    var _a;
    if ((_a = registrationOptions) === null || _a === void 0 ? void 0 : _a.ProviderGroupGuidOverride) {
        throw new Error(error_ProviderGroupOverridesUnsupported);
    }
    registerProviderShared(componentName, false, registrationOptions, deviceInteractionVectorOptions, hasOptionalDataConsentCallback);
};
exports.unregisterProvider = function () {
    var unregisterTime = Date.now();
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    truncateSessionIfActive(telemetryProvider, unregisterTime);
    EventProperties_1.ComponentEventProperties.ActiveSessionSummaries.ClearAll();
    EventProperties_1.ComponentEventProperties.ExtendedData.ClearAll();
    EventProperties_1.ComponentEventProperties.ExtendedData.ActiveUserId = undefined;
    EventProperties_1.ComponentEventProperties.CorrelationVector = undefined;
    telemetryProvider.UnregisterProvider(getCommonProperties());
    telemetryProvider = undefined;
    EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.Reseed();
    EventProperties_1.ClickstreamEventProperties.PageImpressionContext.ClearAll();
};
exports.startGamingTelemetrySession = function (activeUserId, correlationVector, customAttributes) {
    if (activeUserId === void 0) { activeUserId = undefined; }
    if (correlationVector === void 0) { correlationVector = undefined; }
    var startTime = Date.now();
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    if (autoTerminateSessions) {
        truncateSessionIfActive(telemetryProvider, startTime);
    }
    if (!EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
        if (activeUserId) {
            EventProperties_1.ComponentEventProperties.ExtendedData.ActiveUserId = activeUserId;
        }
        if (correlationVector) {
            EventProperties_1.ComponentEventProperties.CorrelationVector = correlationVector;
        }
        sessionStartTime = startTime;
        EventProperties_1.ComponentEventProperties.ExtendedData.SessionId = uuid_1.v4();
    }
    var commonProperties = getCommonProperties();
    telemetryProvider.WriteSessionStart(commonProperties, getPropertiesForEvent(Types_1.GtlEventType.SessionStarted, telemetryProvider, commonProperties), customAttributes);
};
exports.stopGamingTelemetrySession = function (succeeded, sessionDuration, customAttributes) {
    if (sessionDuration === void 0) { sessionDuration = Constants_1.AUTOCOMPUTE_DURATION; }
    var stopTime = Date.now();
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    var sessionState;
    if (EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
        if (succeeded) {
            sessionState = "CompletedSuccess";
        }
        else {
            sessionState = "CompletedFailure";
        }
    }
    else {
        sessionState = "SessionNotRunning";
    }
    var sessionDurationValue;
    if (sessionDuration !== Constants_1.AUTOCOMPUTE_DURATION) {
        sessionDurationValue = sessionDuration;
    }
    else if (sessionStartTime) {
        sessionDurationValue = stopTime - sessionStartTime;
    }
    else {
        sessionDurationValue = 0;
    }
    writeSessionStopAndUpdateSession(telemetryProvider, !succeeded, sessionState, sessionDurationValue, customAttributes);
    EventProperties_1.ComponentEventProperties.ExtendedData.ActiveUserId = undefined;
    EventProperties_1.ComponentEventProperties.CorrelationVector = undefined;
};
exports.setExtendedDataValue = function (key, value) {
    if (key === "activeUserId" || key === "sessionId") {
        throw new Error(error_ExtendedDataReservedKey(key));
    }
    if (EventProperties_1.ComponentEventProperties.ExtendedData.IsFull() &&
        !EventProperties_1.ComponentEventProperties.ExtendedData.HasKey(key)) {
        throw new Error(error_ExtendedDataFull);
    }
    EventProperties_1.ComponentEventProperties.ExtendedData.SetData(key, value);
};
exports.clearExtendedDataValue = function (key) {
    if (!EventProperties_1.ComponentEventProperties.ExtendedData.HasKey(key)) {
        throw new Error(error_ExtendedDataMissingKey(key));
    }
    EventProperties_1.ComponentEventProperties.ExtendedData.SetData(key, undefined);
};
exports.clearExtendedDataValuesExcept = function (excludedKeys) {
    EventProperties_1.ComponentEventProperties.ExtendedData.ClearAll(excludedKeys);
};
exports.resetExtendedData = function () {
    EventProperties_1.ComponentEventProperties.ExtendedData.ClearAll();
};
exports.writeError = function (identifier, message, errorCode, properties, callstack, featureName, customAttributes, isCritical) {
    if (isCritical === void 0) { isCritical = false; }
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    if (identifier === undefined || identifier === null || identifier === "") {
        throw new Error(error_IdentifierMissing);
    }
    var isUserFacingError = false;
    if (properties) {
        if (ErrorPropertyHelpers_1.HasMultipleErrorLevels(properties)) {
            throw new Error(error_MultipleErrorLevels);
        }
        if (ErrorPropertyHelpers_1.HasMultipleErrorTypes(properties)) {
            throw new Error(error_MultipleErrorTypes);
        }
        isUserFacingError = ErrorPropertyHelpers_1.IsUserFacingError(properties);
    }
    if (EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
        EventProperties_1.ComponentEventProperties.ActiveSessionSummaries.TrackError(identifier, errorCode);
    }
    var commonProperties = getCommonProperties();
    var errorPayload = {
        identifier: identifier,
        errorCode: errorCode,
        message: message,
        properties: properties,
        callstack: callstack,
        featureName: featureName,
        customAttributes: customAttributes
    };
    telemetryProvider.WriteError(commonProperties, isUserFacingError
        ? getPropertiesForEventWithDIV(Types_1.GtlEventType.Error, getDivForEvent(telemetryProvider, commonProperties))
        : getPropertiesForEvent(Types_1.GtlEventType.Error, telemetryProvider, commonProperties), isCritical, errorPayload);
};
exports.writePageView = function (pageName, uri, referrerUri, pageViewData, featureName, customAttributes) {
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    var pageImpression = EventProperties_1.ClickstreamEventProperties.PageImpressionContext.TrackPageView(pageName, uri);
    if (!pageViewData) {
        pageViewData = new Types_1.PageViewData();
    }
    var commonProperties = getCommonProperties();
    var pageViewPayload = {
        customAttributes: customAttributes,
        featureName: featureName
    };
    telemetryProvider.WritePageView(commonProperties, getPropertiesForEventWithDIV(Types_1.GtlEventType.PageView, getDivForEvent(telemetryProvider, commonProperties)), pageName, pageImpression, pageViewData, referrerUri, pageViewPayload);
};
exports.writePageAction = function (pageName, behaviorType, targetItemId, pageActionData, featureName, customAttributes, countsAsFeatureUsage) {
    if (countsAsFeatureUsage === void 0) { countsAsFeatureUsage = false; }
    var pageActionTime = Date.now();
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    if (countsAsFeatureUsage) {
        if (!featureName) {
            throw new Error(error_MissingFeatureName);
        }
        if (EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
            EventProperties_1.ComponentEventProperties.ActiveSessionSummaries.TrackFeatureUsage(featureName);
        }
    }
    if (!pageActionData) {
        pageActionData = new Types_1.PageActionData();
    }
    var impressionContext = EventProperties_1.ClickstreamEventProperties.PageImpressionContext.GetContextForPage(pageName);
    var timeToAction = 0;
    if (impressionContext.pageViewTick) {
        timeToAction = pageActionTime - impressionContext.pageViewTick;
    }
    var commonProperties = getCommonProperties();
    var pageActionPayload = {
        countsAsFeatureUsage: countsAsFeatureUsage,
        customAttributes: customAttributes,
        featureName: featureName,
        timeToActionMs: timeToAction
    };
    telemetryProvider.WritePageAction(commonProperties, getPropertiesForEventWithDIV(Types_1.GtlEventType.PageAction, getDivForEvent(telemetryProvider, commonProperties, false)), pageName, behaviorType, pageActionData, impressionContext.impressionId, impressionContext.pageUri, targetItemId, pageActionPayload);
    EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.PendingExtension = true;
};
exports.writeContentUpdate = function (pageName, pageContentData, featureName, customAttributes, contentUpdateData) {
    var _a;
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    var impressionContext = EventProperties_1.ClickstreamEventProperties.PageImpressionContext.GetContextForPage(pageName);
    var commonProperties = getCommonProperties();
    var contentUpdatePayload = {
        customAttributes: customAttributes,
        featureName: featureName
    };
    telemetryProvider.WriteContentUpdate(commonProperties, getPropertiesForEventWithDIV(Types_1.GtlEventType.ContentUpdate, getDivForEvent(telemetryProvider, commonProperties)), pageName, pageContentData, impressionContext.impressionId, impressionContext.pageUri, (_a = contentUpdateData) === null || _a === void 0 ? void 0 : _a.PageType, contentUpdatePayload);
};
exports.writeAppAction = function (actionName, appActionData, featureName, customAttributes, countsAsFeatureUsage, visibleToUser) {
    if (countsAsFeatureUsage === void 0) { countsAsFeatureUsage = false; }
    if (visibleToUser === void 0) { visibleToUser = false; }
    var appActionTime = Date.now();
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    if (countsAsFeatureUsage) {
        if (!featureName) {
            throw new Error(error_MissingFeatureName);
        }
        if (EventProperties_1.ComponentEventProperties.ExtendedData.SessionId) {
            EventProperties_1.ComponentEventProperties.ActiveSessionSummaries.TrackFeatureUsage(featureName);
        }
    }
    var timeToActionMs = 0;
    var pageImpressionId;
    var pageImpressionUri;
    if (!appActionData) {
        appActionData = new Types_1.AppActionData();
    }
    else {
        var foregroundPageName = appActionData.ForegroundPageName;
        if (foregroundPageName) {
            var impressionContext = EventProperties_1.ClickstreamEventProperties.PageImpressionContext.GetContextForPage(foregroundPageName);
            pageImpressionId = impressionContext.impressionId;
            pageImpressionUri = impressionContext.pageUri;
            if (impressionContext.pageViewTick) {
                timeToActionMs = appActionTime - impressionContext.pageViewTick;
            }
        }
    }
    var commonProperties = getCommonProperties();
    var appActionPayload = {
        countsAsFeatureUsage: countsAsFeatureUsage,
        customAttributes: customAttributes,
        featureName: featureName,
        timeToActionMs: timeToActionMs
    };
    telemetryProvider.WriteAppAction(commonProperties, visibleToUser
        ? getPropertiesForEventWithDIV(Types_1.GtlEventType.AppAction, getDivForEvent(telemetryProvider, commonProperties))
        : getPropertiesForEvent(Types_1.GtlEventType.AppAction, telemetryProvider, commonProperties), actionName, appActionData, pageImpressionId, pageImpressionUri, appActionPayload);
};
exports.writeAppLaunch = function (launchType, launchData, customAttributes) {
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    var commonProperties = getCommonProperties();
    var data = { customAttributes: customAttributes };
    telemetryProvider.WriteAppLaunch(commonProperties, getPropertiesForEventWithDIV(Types_1.GtlEventType.AppLaunch, getDivForEvent(telemetryProvider, commonProperties)), launchType, launchData, data);
};
exports.writeEvent = function (name, privacyTags, eventKeyword, properties, persistence, latency, groupIdType) {
    if (persistence === void 0) { persistence = _1ds_core_js_1.EventPersistence.Normal; }
    if (latency === void 0) { latency = _1ds_core_js_1.EventLatency.Normal; }
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    var commonProperties = getCommonProperties();
    var DIV;
    if (isDeviceInteraction(eventKeyword)) {
        DIV = getDivForEvent(telemetryProvider, commonProperties, false);
    }
    telemetryProvider.WriteCustomEvent(commonProperties, {
        eventName: name,
        groupIdType: (groupIdType !== null && groupIdType !== void 0 ? groupIdType : Types_1.GtlGroupIdType.OptionalDiagnostic),
        keyword: eventKeyword,
        privacyTags: privacyTags,
        latency: latency,
        persistence: persistence,
        DIV: DIV
    }, properties);
};
exports.setActiveUserId = function (activeUserId) {
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    if (!activeUserId) {
        throw new Error(error_ActiveUserIdMissing);
    }
    updateActiveUserId(telemetryProvider, activeUserId);
};
exports.clearActiveUserId = function () {
    if (!telemetryProvider) {
        throw new Error(error_Unregistered);
    }
    updateActiveUserId(telemetryProvider, undefined);
};
exports.setCorrelationVector = function (correlationVector) {
    EventProperties_1.ComponentEventProperties.CorrelationVector = correlationVector;
};
exports.clearCorrelationVector = function () {
    EventProperties_1.ComponentEventProperties.CorrelationVector = undefined;
};
exports.getDeviceInteractionVector = function () {
    return EventProperties_1.ClickstreamEventProperties.DeviceInteractionVector.Get();
};
exports.setEventAsRequired = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.GroupIdType.OverrideForEvent(eventType, Types_1.GtlGroupIdType.CoreRequired);
    var eventKeyword;
    if (isDeviceInteraction(EventProperties_1.ComponentEventProperties.Keywords.GetForEvent(eventType))) {
        eventKeyword = Helpers_1.doLargeBitwiseOp([Types_1.EventKeyword.Critical, Types_1.EventKeyword.DeviceInteractionData], "|");
    }
    else {
        eventKeyword = Types_1.EventKeyword.Critical;
    }
    EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, eventKeyword);
};
exports.resetEventToOptional = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    if (EventProperties_1.ComponentEventProperties.GroupIdType.ProviderOverride !== undefined) {
        EventProperties_1.ComponentEventProperties.GroupIdType.OverrideForEvent(eventType, Types_1.GtlGroupIdType.OptionalDiagnostic);
    }
    else {
        EventProperties_1.ComponentEventProperties.GroupIdType.OverrideForEvent(eventType, undefined);
    }
    if (isDeviceInteraction(EventProperties_1.ComponentEventProperties.Keywords.GetForEvent(eventType))) {
        EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, Helpers_1.doLargeBitwiseOp([
            EventProperties_1.ComponentEventProperties.Keywords.GetDefaultForEvent(eventType),
            Types_1.EventKeyword.DeviceInteractionData
        ], "|"));
    }
    else if (EventProperties_1.ComponentEventProperties.Keywords.ProviderOverride !== undefined) {
        EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, EventProperties_1.ComponentEventProperties.Keywords.GetDefaultForEvent(eventType));
    }
    else {
        EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, undefined);
    }
};
exports.overridePrivacyTagForEvent = function (eventType, privacyTag) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.PrivacyTags.OverrideForEvent(eventType, privacyTag);
};
exports.resetPrivacyTagForEvent = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.PrivacyTags.OverrideForEvent(eventType, undefined);
};
exports.overrideKeywordForEvent = function (eventType, eventKeyword) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    else if (EventProperties_1.ComponentEventProperties.GroupIdType.GetForEvent(eventType) ===
        Types_1.GtlGroupIdType.CoreRequired &&
        Helpers_1.doLargeBitwiseOp([eventKeyword, Types_1.EventKeyword.Critical], "&") !==
            Types_1.EventKeyword.Critical) {
        throw new Error(error_InvalidKeywordForRequiredEvent);
    }
    EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, eventKeyword);
};
exports.resetKeywordForEvent = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    if (EventProperties_1.ComponentEventProperties.GroupIdType.GetForEvent(eventType) ===
        Types_1.GtlGroupIdType.CoreRequired) {
        EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, Types_1.EventKeyword.Critical);
    }
    else {
        EventProperties_1.ComponentEventProperties.Keywords.OverrideForEvent(eventType, undefined);
    }
};
exports.overridePersistenceForEvent = function (eventType, persistenceValue) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.Persistence.OverrideForEvent(eventType, persistenceValue);
};
exports.resetPersistenceForEvent = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.Persistence.OverrideForEvent(eventType, undefined);
};
exports.overrideLatencyForEvent = function (eventType, latencyValue) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.Latency.OverrideForEvent(eventType, latencyValue);
};
exports.resetLatencyForEvent = function (eventType) {
    if (eventType === Types_1.GtlEventType.Undefined) {
        throw new Error(error_PropertyChangeForInvalidEvent(eventType));
    }
    EventProperties_1.ComponentEventProperties.Latency.OverrideForEvent(eventType, undefined);
};
//# sourceMappingURL=GamingTelemetry.js.map